// @ts-nocheck
import React, { useState, useRef } from 'react';
import { Image, X, Loader2, AlertTriangle, Info } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { preFilterContent, ContentFilterResult } from '@/utils/contentFilter';

interface PostComposerProps {
  onPostCreated?: () => void;
}

export function PostComposer({ onPostCreated }: PostComposerProps) {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [contentWarning, setContentWarning] = useState<ContentFilterResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const authorName = profile?.first_name && profile?.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : user?.email?.charAt(0).toUpperCase() || 'U';

  const authorInitials = typeof authorName === 'string'
    ? authorName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  // Check content as user types (debounced effect)
  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    
    // Only check if content is substantial
    if (newContent.trim().length > 10) {
      const filterResult = preFilterContent(newContent);
      setContentWarning(filterResult.blocked ? filterResult : null);
    } else {
      setContentWarning(null);
    }
  };

  const createPost = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Must be logged in to post');
      if (!content.trim() && mediaFiles.length === 0) {
        throw new Error('Post cannot be empty');
      }

      // Final pre-filter check before posting
      const filterResult = preFilterContent(content);
      if (filterResult.blocked) {
        throw new Error(`This content may not be appropriate for our sports community. ${filterResult.reason}`);
      }

      // Upload media files if any
      let mediaUrls: string[] = [];
      
      if (mediaFiles.length > 0) {
        for (const file of mediaFiles) {
          const fileExt = file.name.split('.').pop();
          const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
          
          const { error: uploadError, data } = await supabase.storage
            .from('wall-media')
            .upload(fileName, file);

          if (uploadError) {
            console.error('Upload error:', uploadError);
            throw new Error('Failed to upload media');
          }

          const { data: { publicUrl } } = supabase.storage
            .from('wall-media')
            .getPublicUrl(fileName);

          mediaUrls.push(publicUrl);
        }
      }

      // Extract hashtags from content
      const hashtagMatches = content.match(/#[\w]+/g) || [];
      const hashtags = hashtagMatches.map(tag => tag.slice(1));

      // Create the post
      const { error, data } = await supabase
        .from('wall_posts')
        .insert({
          profile_id: user.id,
          content: content.trim(),
          media_urls: mediaUrls.length > 0 ? mediaUrls : null,
          hashtags: hashtags.length > 0 ? hashtags : null,
          post_type: 'standard',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setContent('');
      setMediaFiles([]);
      setMediaPreviews([]);
      queryClient.invalidateQueries({ queryKey: ['wall-feed'] });
      toast.success('Post created!');
      onPostCreated?.();
    },
    onError: (error: Error) => {
      console.error('Create post error:', error);
      toast.error(error.message || 'Failed to create post');
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Limit to 4 files
    const newFiles = files.slice(0, 4 - mediaFiles.length);
    
    setMediaFiles(prev => [...prev, ...newFiles]);

    // Create previews
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeMedia = (index: number) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
    setMediaPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const canPost = (content.trim().length > 0 || mediaFiles.length > 0) && !createPost.isPending;

  return (
    <div className="bg-card border-b border-border p-4">
      <div className="flex gap-3">
        <Avatar className="h-10 w-10 flex-shrink-0">
          <AvatarImage src={profile?.avatar_url || undefined} />
          <AvatarFallback className="bg-primary text-primary-foreground text-sm">
            {authorInitials}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 space-y-3">
          {/* Sports-only reminder */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Info className="h-3 w-3" />
            <span>Keep it sports! 🏆 </span>
            <Link to="/community-guidelines" className="text-primary hover:underline">
              Community Guidelines
            </Link>
          </div>

          <Textarea
            placeholder="Share your training, highlights, or recruiting updates..."
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            className="min-h-[80px] resize-none border-0 bg-transparent p-0 text-foreground placeholder:text-muted-foreground focus-visible:ring-0"
            maxLength={500}
          />

          {/* Content Warning */}
          {contentWarning && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-amber-800 dark:text-amber-300">
                <p className="font-medium">This content may not be appropriate</p>
                <p className="text-xs mt-1">
                  {contentWarning.reason}. Please keep posts sports-related. 
                  <Link to="/community-guidelines" className="ml-1 underline">
                    View guidelines
                  </Link>
                </p>
              </div>
            </div>
          )}

          {/* Media Previews */}
          {mediaPreviews.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {mediaPreviews.map((preview, index) => (
                <div key={index} className="relative rounded-lg overflow-hidden">
                  {mediaFiles[index]?.type.startsWith('video/') ? (
                    <video src={preview} className="w-full aspect-square object-cover" />
                  ) : (
                    <img src={preview} alt="" className="w-full aspect-square object-cover" />
                  )}
                  <button
                    type="button"
                    onClick={() => removeMedia(index)}
                    className="absolute top-1 right-1 bg-background/80 rounded-full p-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={mediaFiles.length >= 4}
              >
                <Image className="h-5 w-5" />
              </Button>
            </div>

            <div className="flex items-center gap-2">
              {content.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {content.length}/500
                </span>
              )}
              <Button
                size="sm"
                onClick={() => createPost.mutate()}
                disabled={!canPost}
              >
                {createPost.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Post'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
